/**
 * School-level password provisioning (general_settings.password_provisioning).
 *
 * 'default' — every new user gets the legacy default password.
 * 'random'  — every new user gets a unique generated password. The API never
 *             stores the plaintext: it comes back once on the create /
 *             import / regenerate response (`credentials`), and is emailed to
 *             the user only when the admin explicitly asked (`send_email`).
 *             Backend: ilmversity-node docs/password-provisioning.md
 */
import {CustomDownloadFile, hasPermission, isAdmin} from 'common/HelperFunctions'

export const PASSWORD_PROVISIONING_DEFAULT = 'default'
export const PASSWORD_PROVISIONING_RANDOM = 'random'

export type PasswordProvisioningMode =
  | typeof PASSWORD_PROVISIONING_DEFAULT
  | typeof PASSWORD_PROVISIONING_RANDOM

/** Error code the API returns (HTTP 403) while a generated password is unchanged. */
export const PASSWORD_CHANGE_REQUIRED_CODE = 'PASSWORD_CHANGE_REQUIRED'

/** Why a generated password was not emailed (mirrors emailDeliverability.EMAIL_REASONS). */
export type CredentialReason =
  | 'no_email'
  | 'placeholder_email'
  | 'invalid_syntax'
  | 'domain_no_mx'
  | 'email_disabled'
  | 'send_failed'
  | 'no_template'

export const CREDENTIAL_REASON_KEYS: Record<CredentialReason, string> = {
  no_email: 'USER.CREDENTIAL_REASON.NO_EMAIL',
  placeholder_email: 'USER.CREDENTIAL_REASON.PLACEHOLDER_EMAIL',
  invalid_syntax: 'USER.CREDENTIAL_REASON.INVALID_SYNTAX',
  domain_no_mx: 'USER.CREDENTIAL_REASON.DOMAIN_NO_MX',
  email_disabled: 'USER.CREDENTIAL_REASON.EMAIL_DISABLED',
  send_failed: 'USER.CREDENTIAL_REASON.SEND_FAILED',
  no_template: 'USER.CREDENTIAL_REASON.NO_TEMPLATE',
}

/** Reasons that mean "the address itself is the problem" — worth a warning. */
export const EMAIL_PROBLEM_REASONS: CredentialReason[] = [
  'no_email',
  'placeholder_email',
  'invalid_syntax',
  'domain_no_mx',
]

export type GeneratedCredential = {
  userId: number
  username: string
  email: string | null
  /** Plaintext, present on every entry. Exists only in this response. */
  password: string
  emailed: boolean
  reason: CredentialReason | null
}

export type CredentialsSummary = {
  /** The admin ticked "send email". */
  emailRequested: boolean
  total: number
  emailed: number
  entries: GeneratedCredential[]
}

export type RegenerateUserType = 'student' | 'parent' | 'teacher' | 'staff'

/**
 * Normalise whatever general_settings holds to a known mode.
 * @param generalSettings the mainReducer.generalSettings object (or undefined)
 */
export function resolvePasswordProvisioningMode(
  generalSettings?: {password_provisioning?: unknown} | null
): PasswordProvisioningMode {
  const raw = String(generalSettings?.password_provisioning ?? '')
    .trim()
    .toLowerCase()
  return raw === PASSWORD_PROVISIONING_RANDOM
    ? PASSWORD_PROVISIONING_RANDOM
    : PASSWORD_PROVISIONING_DEFAULT
}

/** True when new users of this school get generated passwords. */
export function isRandomPasswordProvisioning(
  generalSettings?: {password_provisioning?: unknown} | null
): boolean {
  return resolvePasswordProvisioningMode(generalSettings) === PASSWORD_PROVISIONING_RANDOM
}

/** Mirrors the API-side check in passwordProvisioning.canRegeneratePasswords. */
export function canRegeneratePasswords(): boolean {
  return isAdmin() || hasPermission('user_manager') || hasPermission('user_manager_update')
}

/**
 * Does an API failure mean "change your generated password first"?
 * Matches the 403 body userAuth returns: `{statusCode: 403, result: {code}}`.
 */
export function isPasswordChangeRequiredError(err: unknown): boolean {
  const e = err as {statusCode?: number; result?: {code?: string}} | null
  return e?.statusCode === 403 && e?.result?.code === PASSWORD_CHANGE_REQUIRED_CODE
}

/**
 * Normalise a `credentials` object from any API response (create, CSV upload,
 * bulk import, regenerate). Returns null when nothing was generated.
 */
export function normalizeCredentialsSummary(raw: unknown): CredentialsSummary | null {
  const r = raw as Partial<CredentialsSummary> | null | undefined
  if (!r || !Array.isArray(r.entries) || r.entries.length === 0) return null
  const entries: GeneratedCredential[] = r.entries
    .filter((e): e is GeneratedCredential => Boolean(e && typeof e === 'object'))
    .map((e) => ({
      userId: Number(e.userId),
      username: String(e.username ?? ''),
      email: e.email ? String(e.email) : null,
      password: String(e.password ?? ''),
      emailed: Boolean(e.emailed),
      reason: e.emailed ? null : ((e.reason as CredentialReason | null) ?? null),
    }))
  return {
    emailRequested: Boolean(r.emailRequested),
    total: typeof r.total === 'number' ? r.total : entries.length,
    emailed: typeof r.emailed === 'number' ? r.emailed : entries.filter((e) => e.emailed).length,
    entries,
  }
}

/** Combine the summaries of several API calls (e.g. parent + student create). */
export function mergeCredentialsSummaries(
  ...summaries: Array<CredentialsSummary | null | undefined>
): CredentialsSummary | null {
  const present = summaries.filter((s): s is CredentialsSummary => Boolean(s))
  if (present.length === 0) return null
  const entries = present.flatMap((s) => s.entries)
  return {
    emailRequested: present.some((s) => s.emailRequested),
    total: entries.length,
    emailed: entries.filter((e) => e.emailed).length,
    entries,
  }
}

const csvCell = (value: unknown): string => {
  const text = String(value ?? '')
  // Neutralise spreadsheet formula injection and quote as needed.
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

/**
 * Build the one-time credentials CSV (username, password, email, emailed, reason).
 * Pure; the caller decides when to download.
 */
export function credentialsToCsv(
  entries: GeneratedCredential[],
  reasonLabel: (reason: CredentialReason | null) => string = (r) => r ?? ''
): string {
  const header = ['Username', 'Password', 'Email', 'Emailed', 'Note']
  const rows = entries.map((e) => [
    e.username,
    e.password,
    e.email ?? '',
    e.emailed ? 'yes' : 'no',
    e.emailed ? '' : reasonLabel(e.reason),
  ])
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n'
}

export function downloadCredentialsCsv(
  entries: GeneratedCredential[],
  filename = 'login-credentials.csv',
  reasonLabel?: (reason: CredentialReason | null) => string
): void {
  // BOM so Excel opens UTF-8 usernames correctly.
  CustomDownloadFile(
    String.fromCharCode(0xfeff) + credentialsToCsv(entries, reasonLabel),
    filename,
    'text/csv;charset=utf-8;'
  )
}
